import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCNPJ, formatCPF } from '../../../shared/utils/validators';
import type { ExistingClienteRef } from '../api';

interface Props {
  existingClient: ExistingClienteRef;
  onClose: () => void;
}

/**
 * Modal exibido quando o usuário tenta cadastrar um cliente cujo
 * documento (CNPJ/CPF) já existe (Cenário 2, Spec 01).
 *
 * Bloqueio absoluto: não permite forçar a criação; convida a abrir o
 * cliente existente ou voltar para corrigir o formulário.
 */
export const ClienteDuplicadoModal: React.FC<Props> = ({ existingClient, onClose }) => {
  const navigate = useNavigate();

  const isCnpj = existingClient.documento.length === 14;
  const docFormatado = isCnpj ? formatCNPJ(existingClient.documento) : formatCPF(existingClient.documento);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div
        className="w-full max-w-lg rounded-lg bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Cliente já cadastrado"
      >
        <div className="flex items-start justify-between border-b border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-yellow-100 p-2 text-yellow-700">
              <AlertTriangle size={22} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Cliente já cadastrado</h3>
              <p className="text-sm text-gray-500">
                Existe um cliente com este {isCnpj ? 'CNPJ' : 'CPF'} na base.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3 p-6">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div>
                <div className="text-xs uppercase text-gray-500">Código</div>
                <div className="font-mono text-sm text-gray-900">{existingClient.code}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Situação</div>
                <div className="text-sm text-gray-900">{existingClient.situacaoCadastral}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-xs uppercase text-gray-500">Razão Social</div>
                <div className="text-sm text-gray-900">{existingClient.razaoSocial}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-xs uppercase text-gray-500">{isCnpj ? 'CNPJ' : 'CPF'}</div>
                <div className="font-mono text-sm text-gray-900">{docFormatado}</div>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            Você pode abrir o cadastro existente para consultá-lo ou voltar ao formulário para
            corrigir o documento.
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Voltar ao formulário
          </button>
          <button
            type="button"
            onClick={() => navigate(`/clientes/${existingClient.id}`)}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Ver cliente existente
          </button>
        </div>
      </div>
    </div>
  );
};
